/**
 * Local persistence. No server, no account — everything lives in localStorage.
 *
 * A puzzle is stored whole rather than as a seed. Regenerating from a seed would
 * mean re-running the search on every load, and the search is time-boxed, so the
 * same seed can legitimately produce a different board on a slower device.
 */

import { DEFAULT_PALETTE, isPalette, type Palette } from './theme';
import {
  DIFFICULTIES,
  LIGHT,
  SHADOW,
  UNKNOWN,
  type Difficulty,
  type Mark,
  type Puzzle,
} from './types';

const KEY = 'lightshadow.v1';

export interface SavedGame {
  puzzle: Puzzle;
  marks: Mark[];
  /** Seconds of play, accumulated across visits. */
  elapsed: number;
  completed: boolean;
}

export interface Stored {
  difficulty: Difficulty;
  /** The colours the board is drawn in, kept across puzzles rather than per game. */
  palette: Palette;
  game: SavedGame | null;
  /** Best time in seconds, keyed by difficulty. */
  best: Partial<Record<Difficulty, number>>;
}

export const DEFAULT_DIFFICULTY: Difficulty = 'easy';

function isPuzzle(value: unknown): value is Puzzle {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<Puzzle>;
  if (typeof candidate.width !== 'number' || typeof candidate.height !== 'number') return false;
  const cells = candidate.width * candidate.height;
  return (
    Array.isArray(candidate.solution) &&
    candidate.solution.length === cells &&
    Array.isArray(candidate.clues) &&
    candidate.clues.every(
      (clue) =>
        typeof clue?.cell === 'number' &&
        clue.cell >= 0 &&
        clue.cell < cells &&
        // Null is a `?`, and has to survive the round trip through JSON like any
        // other value: a board that came back with its hidden numbers filled in
        // would be a different board.
        (clue.value === null || typeof clue.value === 'number') &&
        (clue.shade === LIGHT || clue.shade === SHADOW),
    ) &&
    DIFFICULTIES.includes(candidate.difficulty as Difficulty)
  );
}

function readMarks(value: unknown, cells: number): Mark[] | null {
  if (!Array.isArray(value) || value.length !== cells) return null;
  return value.map((mark) => (mark === LIGHT || mark === SHADOW ? mark : UNKNOWN));
}

/**
 * Reads saved state, discarding anything that does not look right.
 *
 * Storage is shared with whatever the browser had before and survives across
 * versions of this code, so every field is checked rather than trusted; a bad
 * record costs the player one puzzle, an unhandled exception costs them the page.
 */
export function load(): Stored {
  const fallback: Stored = {
    difficulty: DEFAULT_DIFFICULTY,
    palette: { ...DEFAULT_PALETTE },
    game: null,
    best: {},
  };
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return fallback; // private browsing, or storage disabled
  }
  if (!raw) return fallback;

  try {
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (DIFFICULTIES.includes(parsed.difficulty as Difficulty)) {
      fallback.difficulty = parsed.difficulty as Difficulty;
    }
    // Anything that is not three colours is dropped whole rather than merged: a
    // half-read palette is a board painted in two schemes at once.
    if (isPalette(parsed.palette)) fallback.palette = parsed.palette;
    const game = parsed.game;
    if (game && isPuzzle(game.puzzle)) {
      const marks = readMarks(game.marks, game.puzzle.width * game.puzzle.height);
      if (marks) {
        fallback.game = {
          puzzle: game.puzzle,
          marks,
          elapsed: typeof game.elapsed === 'number' ? game.elapsed : 0,
          completed: game.completed === true,
        };
      }
    }
    if (parsed.best && typeof parsed.best === 'object') {
      for (const difficulty of DIFFICULTIES) {
        const value = parsed.best[difficulty];
        if (typeof value === 'number' && Number.isFinite(value)) fallback.best[difficulty] = value;
      }
    }
  } catch {
    return fallback;
  }
  return fallback;
}

export function save(state: Stored): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // A full or disabled store is not worth interrupting play over.
  }
}
